<script setup lang="ts" generic="T extends string | number">
import {computed, nextTick, onBeforeUnmount, ref, watch} from 'vue';
import IconArrowDown from '~icons/mdi/chevron-down';

defineOptions({name: 'Dropdown', inheritAttrs: false});

interface DropdownOption<V extends string | number> {
    value: V;
    label: string;
    disabled?: boolean;
}

const props = withDefaults(defineProps<{
    modelValue: T | null;
    options: ReadonlyArray<DropdownOption<T>>;
    size?: 'm' | 's';
    /**
     * Text alignment for both the trigger label and the option list.
     * Defaults to 'left' — set to 'center' or 'right' when the surrounding
     * layout demands a different visual rhythm.
     */
    align?: 'left' | 'center' | 'right';
    disabled?: boolean;
    placeholder?: string;
    ariaLabel?: string;
    /**
     * When set, each option gets `data-test="<prefix>-<value>"`. The trigger
     * uses the consumer's own `data-test` (via $attrs).
     */
    optionTestPrefix?: string;
}>(), {
    size: 'm',
    align: 'left',
    disabled: false,
});

const emit = defineEmits<{
    'update:modelValue': [value: T];
}>();

const isOpen = ref(false);
const wrapperRef = ref<HTMLDivElement | null>(null);
const triggerRef = ref<HTMLButtonElement | null>(null);
const panelRef = ref<HTMLUListElement | null>(null);
const focusedIndex = ref(-1);

const selectedLabel = computed(
    () => props.options.find(o => o.value === props.modelValue)?.label ?? props.placeholder ?? ''
);

function open() {
    if (props.disabled) return;
    isOpen.value = true;
    focusedIndex.value = props.options.findIndex(o => o.value === props.modelValue);
    if (focusedIndex.value < 0) focusedIndex.value = 0;
}

function close() {
    isOpen.value = false;
    focusedIndex.value = -1;
}

function toggle() {
    if (isOpen.value) close();
    else open();
}

function selectOption(option: DropdownOption<T>) {
    if (option.disabled) return;
    emit('update:modelValue', option.value);
    close();
    triggerRef.value?.focus();
}

function moveFocus(delta: number) {
    if (props.options.length === 0) return;
    const len = props.options.length;
    let next = focusedIndex.value;
    for (let i = 0; i < len; i++) {
        next = (next + delta + len) % len;
        if (!props.options[next].disabled) break;
    }
    focusedIndex.value = next;
    nextTick(() => {
        const el = panelRef.value?.children[next] as HTMLElement | undefined;
        el?.focus();
    });
}

function onTriggerKeydown(event: KeyboardEvent) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        if (!isOpen.value) open();
        nextTick(() => moveFocus(event.key === 'ArrowDown' ? 1 : -1));
    } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        toggle();
    }
}

function onOptionKeydown(event: KeyboardEvent, option: DropdownOption<T>) {
    if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        selectOption(option);
    } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        moveFocus(1);
    } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        moveFocus(-1);
    } else if (event.key === 'Tab') {
        // Let tab close the panel and continue natural focus order.
        close();
    }
}

function onDocumentClick(event: MouseEvent) {
    const target = event.target as Node | null;
    if (target && !wrapperRef.value?.contains(target)) {
        close();
    }
}

function onDocumentKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && isOpen.value) {
        close();
        triggerRef.value?.focus();
    }
}

watch(isOpen, (open) => {
    if (open) {
        // Defer so this same click doesn't bubble straight to the document
        // listener and close the panel we just opened.
        setTimeout(() => document.addEventListener('click', onDocumentClick), 0);
        document.addEventListener('keydown', onDocumentKeydown);
    } else {
        document.removeEventListener('click', onDocumentClick);
        document.removeEventListener('keydown', onDocumentKeydown);
    }
});

onBeforeUnmount(() => {
    document.removeEventListener('click', onDocumentClick);
    document.removeEventListener('keydown', onDocumentKeydown);
});

function optionDataTest(option: DropdownOption<T>): string | undefined {
    return props.optionTestPrefix ? `${props.optionTestPrefix}-${option.value}` : undefined;
}
</script>

<template>
    <div
        ref="wrapperRef"
        class="merit-dropdown"
        :class="[`merit-dropdown--${size}`, {'merit-dropdown--disabled': disabled, 'merit-dropdown--open': isOpen}]"
        :style="{'--merit-dropdown-align': align}"
    >
        <button
            ref="triggerRef"
            type="button"
            class="merit-dropdown__trigger"
            :disabled="disabled"
            :aria-label="ariaLabel"
            aria-haspopup="listbox"
            :aria-expanded="isOpen"
            v-bind="$attrs"
            @click="toggle"
            @keydown="onTriggerKeydown"
        >
            <span class="merit-dropdown__label">{{ selectedLabel }}</span>
            <IconArrowDown class="merit-dropdown__arrow" aria-hidden="true"/>
        </button>
        <ul
            v-if="isOpen"
            ref="panelRef"
            class="merit-dropdown__panel"
            role="listbox"
            :aria-label="ariaLabel"
        >
            <li
                v-for="(option, index) in options"
                :key="option.value"
                role="option"
                :aria-selected="option.value === modelValue"
                :aria-disabled="option.disabled || undefined"
                class="merit-dropdown__option"
                :class="{
                    'merit-dropdown__option--selected': option.value === modelValue,
                    'merit-dropdown__option--focused': index === focusedIndex,
                    'merit-dropdown__option--disabled': option.disabled,
                }"
                :data-test="optionDataTest(option)"
                :tabindex="option.disabled ? -1 : 0"
                @click="selectOption(option)"
                @keydown="onOptionKeydown($event, option)"
            >
                {{ option.label }}
            </li>
        </ul>
    </div>
</template>

<style scoped>
/* Prefix with `merit-` because the legacy stylesheet at
   Palk/Resources/Internal/Css/Dropdown.css owns the bare `.dropdown` class
   and sets `pointer-events: none` on it (clicks are only enabled via
   `.dropdown.ready`). Vue's scoped data-v boost wins on specificity for
   properties we override, but the legacy `pointer-events: none` cascades
   through any property we don't redeclare — making the whole control
   silently non-interactive. */
.merit-dropdown {
    position: relative;
    display: block;
    width: 100%;
}

.merit-dropdown__trigger {
    width: 100%;
    margin: 0;
    border: 1px solid #DFE1E6;
    background-color: #fff;
    color: #000;
    font-family: inherit;
    text-align: var(--merit-dropdown-align, left);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    box-shadow: none;
    outline: none;
    box-sizing: border-box;
}

.merit-dropdown__trigger:hover,
.merit-dropdown__trigger:focus,
.merit-dropdown--open .merit-dropdown__trigger {
    border-color: #2EB336;
    outline: none;
}

.merit-dropdown--disabled .merit-dropdown__trigger,
.merit-dropdown__trigger:disabled {
    background-color: #EAECF2;
    color: #727272;
    cursor: not-allowed;
    pointer-events: none;
}

.merit-dropdown__label {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.merit-dropdown__arrow {
    flex-shrink: 0;
    /* MDI icons render as <svg> with fill="currentColor", so the arrow
       inherits the trigger's text color: #000 by default, #727272 in the
       disabled state. */
    transition: transform 0.2s ease;
}

.merit-dropdown--open .merit-dropdown__arrow {
    transform: rotate(-180deg);
}

/* Base panel matches the legacy SelectWidget look: attached to the trigger
   (no gap), darker border, no radius/shadow. The size 's' variant overrides
   below to lift the panel away from the trigger and soften the chrome. */
.merit-dropdown__panel {
    position: absolute;
    top: calc(100% + 1px);
    left: 0;
    right: 0;
    margin: 0;
    padding: 0;
    list-style: none;
    background-color: #fff;
    border: 1px solid #727272;
    border-top: 0;
    max-height: 252px;
    overflow-y: auto;
    z-index: 100;
    box-sizing: border-box;
}

.merit-dropdown__option {
    color: #000;
    cursor: pointer;
    /* Defend against centered text-align inherited from the parent .logic-div
       on the login card (.authent-scope-2020 .logic-div { text-align: center; }).
       Consumer picks via the `align` prop; defaults to left. */
    text-align: var(--merit-dropdown-align, left);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    outline: none;
}

/* Hover and keyboard focus share the lighter blue (#E6F6FF); selected stays
   on the slightly stronger #D8F0FD so the active row remains distinct from
   whatever the pointer / arrow keys are currently over. Matches the legacy
   SelectWidget convention. */
.merit-dropdown__option:hover,
.merit-dropdown__option--focused {
    background-color: #E6F6FF;
}

.merit-dropdown__option--selected {
    background-color: #D8F0FD;
}

.merit-dropdown__option--disabled {
    color: #999;
    cursor: not-allowed;
    pointer-events: none;
}

/* Size: m (medium / normal, matches legacy SelectWidget) */
.merit-dropdown--m .merit-dropdown__trigger {
    height: 36px;
    padding: 0 15px;
    font-size: 14px;
}

.merit-dropdown--m .merit-dropdown__option {
    padding: 9px 15px;
    font-size: 14px;
}

.merit-dropdown--m .merit-dropdown__arrow {
    width: 18px;
    height: 18px;
}

/* Size: s (small, used for compact controls like login language picker) */
.merit-dropdown--s .merit-dropdown__trigger {
    height: 26px;
    padding: 0 10px;
    font-size: 12px;
    border-radius: 1px;
}

.merit-dropdown--s .merit-dropdown__option {
    padding: 8px 12px;
    font-size: 12px;
}

.merit-dropdown--s .merit-dropdown__arrow {
    width: 18px;
    height: 18px;
}

/* Size 's' panel is detached from the trigger and softer chrome — used for
   compact navigational pickers (e.g., login language switcher) where the
   attached legacy look would feel heavy. */
.merit-dropdown--s .merit-dropdown__panel {
    top: calc(100% + 6px);
    padding: 6px 0;
    border: 1px solid #DFE1E6;
    border-radius: 2px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}
</style>
